/* eslint-disable @typescript-eslint/no-explicit-any */
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { GraphQLClient } from "graphql-request";
import type { VariablesOf, ResultOf } from "gql.tada";
import type { FetchError } from "./types";
import { isQuery, mapError } from "./utils/common";
import { MAX_INFLIGHT_REQUESTS, REQUEST_TIMEOUT } from "./constants";
import { getCacheKey } from "./utils";
import { getBackendUrl } from "utils/getBackendUrl";

let endpoint: string | null = null;
let client: GraphQLClient | null = null;

function getClient(): GraphQLClient {
  if (!client) {
    const backendUrl = getBackendUrl();
    if (!backendUrl) {
      throw new Error("Backend URL is not defined");
    }
    endpoint = `${backendUrl}/graphql`;
    client = new GraphQLClient(endpoint, {
      credentials: "include", // Include cookies for authentication
    });
  }
  return client;
}

// Deferred promise for handling multiple callers on same in-flight request
type Deferred<T> = {
  promise: Promise<T>;
  resolve: (value: T) => void;
  reject: (reason?: any) => void;
};

function createDeferred<T>(): Deferred<T> {
  let resolve!: (value: T) => void;
  let reject!: (reason?: any) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

type InflightEntry<T> = {
  deferred: Deferred<T>;
  controller: AbortController;
};

const inFlightRequests = new Map<string, InflightEntry<any>>();

export const gqlFetch = async <Query extends TypedDocumentNode<any, any>>({
  query,
  variables,
  signal,
  timeout = REQUEST_TIMEOUT,
}: {
  query: Query;
  variables?: VariablesOf<Query>;
  signal?: AbortSignal;
  timeout?: number;
}): Promise<ResultOf<Query>> => {
  // Log variables at entry point to debug dedup issues
  const definition = query.definitions?.[0];
  const operationName =
    definition?.kind === "OperationDefinition" ? definition.name?.value : "?";
  console.warn(
    "[gqlFetch] Entry - operation:",
    operationName,
    "variables:",
    variables,
  );

  // Only deduplicate queries; mutations/subscriptions bypass dedup
  const shouldDedup = isQuery(query);
  const key = shouldDedup ? getCacheKey(query, variables) : null;

  // If in-flight query exists, abort it and start fresh
  // Reuse the existing deferred so all waiting callers stay
  let deferred: Deferred<ResultOf<Query>> | null = null;
  if (shouldDedup && key) {
    const existing = inFlightRequests.get(key);
    if (existing) {
      // Abort the old request but reuse its deferred
      // so existing callers stay attached and get the new result
      existing.controller.abort();
      deferred = existing.deferred;
      inFlightRequests.delete(key);
    }
  }

  // Own controller for timeout — separate from the caller's signal
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(new Error("Request timed out")),
    timeout,
  );

  // Chain the caller's signal so either one can cancel the fetch
  signal?.addEventListener("abort", () => controller.abort(signal.reason));

  // For queries, create new deferred only if we didn't reuse one from superseded request
  if (!deferred && shouldDedup) {
    deferred = createDeferred<ResultOf<Query>>();
  }
  if (shouldDedup && key && deferred) {
    inFlightRequests.set(key, { deferred, controller: controller });
    // Prevent unbounded map growth: prune oldest entries if over limit
    if (inFlightRequests.size > MAX_INFLIGHT_REQUESTS) {
      const firstKey = inFlightRequests.keys().next().value;
      if (firstKey) inFlightRequests.delete(firstKey);
    }
  }

  const executeRequest = async (): Promise<ResultOf<Query>> => {
    try {
      console.warn(
        "[gqlFetch] Before client.request - variables being sent:",
        variables,
      );
      const result = await getClient().request<ResultOf<Query>>({
        document: query,
        variables,
        signal: controller.signal,
      });
      return result;
    } catch (err) {
      // Unwrap the abort reason so callers get a meaningful error
      if (controller.signal.aborted) {
        const reason =
          controller.signal.reason instanceof Error
            ? controller.signal.reason
            : new Error("Request aborted");
        throw mapError(reason);
      }
      throw mapError(err);
    } finally {
      clearTimeout(timeoutId); // always clear, win or lose
    }
  };

  // Non-deduplicated path (mutations, subscriptions)
  if (!deferred) {
    return executeRequest();
  }

  // Deduplicated path: resolve/reject deferred, clean up on completion
  try {
    const result = await executeRequest();
    if (result === undefined) {
      console.warn(
        "[gqlFetch] ⚠️ Resolving deferred with UNDEFINED - key:",
        key,
      );
    } else {
      console.warn(
        "[gqlFetch] ✓ Resolving deferred with valid data - key:",
        key,
      );
    }
    deferred.resolve(result);
    if (key && inFlightRequests.get(key)?.controller === controller) {
      inFlightRequests.delete(key);
    }
    return result;
  } catch (error) {
    const err = error as FetchError; // already mapped in executeRequest
    // Only reject deferred if THIS controller is still the active one.
    // If it's been superseded, a new controller now owns the deferred.
    if (key && inFlightRequests.get(key)?.controller === controller) {
      if ("not_authenticated" !== err.type) {
        deferred.reject(err);
      }
      inFlightRequests.delete(key);
      throw err;
    } else {
      // We've been superseded by a newer request.
      // Return the shared deferred so caller waits on the new request.
      console.warn(
        "[gqlFetch] ↻ Superseded - returning deferred.promise for key:",
        key,
      );
      return deferred.promise;
    }
  }
};
